import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { pricesApi } from '@/shared/api';
import type {
  PriceCategoryWithItems,
  PriceItemWithRelations
} from '@/shared/types';

/**
 * Страница управления ценами
 * Отображает категории и услуги с возможностью управления
 */
export const PricesPage: React.FC = () => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>(
    'categories'
  );

  // Хуки для уведомлений
  const { showSuccess, showError } = useNotificationActions();

  // Загрузка категорий
  const {
    data: categoriesResponse,
    error: categoriesError,
    isLoading: isLoadingCategories,
    mutate: mutateCategories
  } = useSWR(
    ['price-categories', includeInactive],
    () => pricesApi.getAllCategories({ includeInactive }),
    { refreshInterval: 30000 }
  );

  // Загрузка услуг
  const {
    data: itemsResponse,
    error: itemsError,
    isLoading: isLoadingItems,
    mutate: mutateItems
  } = useSWR(
    ['price-items', includeInactive],
    () => pricesApi.getAllItems({ includeInactive }),
    { refreshInterval: 30000 }
  );

  const categories = categoriesResponse?.categories || [];
  const items = itemsResponse?.items || [];

  /**
   * Обработчик изменения статуса категории
   */
  const handleCategoryStatusChange = async (
    category: PriceCategoryWithItems
  ): Promise<void> => {
    try {
      await pricesApi.updateCategoryStatus(category.id, !category.isActive);
      await mutateCategories();
      showSuccess(
        `Категория "${category.name}" успешно ${category.isActive ? 'деактивирована' : 'активирована'}`
      );
    } catch (error) {
      console.error('Error updating category status:', error);
      showError('Ошибка при изменении статуса категории');
    }
  };

  /**
   * Обработчик изменения статуса услуги
   */
  const handleItemStatusChange = async (
    item: PriceItemWithRelations
  ): Promise<void> => {
    try {
      await pricesApi.updateItemStatus(item.id, !item.isActive);
      await mutateItems();
      showSuccess(
        `Услуга "${item.name}" успешно ${item.isActive ? 'деактивирована' : 'активирована'}`
      );
    } catch (error) {
      console.error('Error updating item status:', error);
      showError('Ошибка при изменении статуса услуги');
    }
  };

  /**
   * Форматирование цены
   */
  const formatPrice = (price: string | null): string => {
    if (price === null || price === '') return '—';
    const numPrice = Number(price);
    if (isNaN(numPrice)) return price; // Возвращаем как есть, если не число
    return `${numPrice.toLocaleString('ru-RU')} ₽`;
  };

  return (
    <Layout title="Управление ценами">
      <div className="space-y-6">
        {/* Заголовок и кнопки добавления */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Управление ценами
            </h2>
            <p className="text-base-content/60 mt-1">
              Управление категориями цен и услугами клиники
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/prices/categories/create" className="btn btn-primary">
              <i className="fas fa-plus"></i>
              Добавить категорию
            </Link>
            <Link to="/prices/items/create" className="btn btn-secondary">
              <i className="fas fa-plus"></i>
              Добавить услугу
            </Link>
          </div>
        </div>

        {/* Фильтры */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeInactive"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="checkbox checkbox-primary"
                />
                <label
                  htmlFor="includeInactive"
                  className="text-sm font-medium"
                >
                  Показать неактивные
                </label>
              </div>

              {/* Переключатель вкладок */}
              <div className="tabs tabs-boxed">
                <button
                  className={`tab ${activeTab === 'categories' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('categories')}
                >
                  <i className="fas fa-list mr-2"></i>
                  Категории
                </button>
                <button
                  className={`tab ${activeTab === 'items' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('items')}
                >
                  <i className="fas fa-tags mr-2"></i>
                  Услуги
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Содержимое вкладок */}
        {activeTab === 'categories' && (
          <div className="card card-bordered bg-base-100 shadow-sm">
            <div className="card-body p-2">
              <h3 className="text-lg font-semibold text-base-content mb-4">
                Категории цен
              </h3>

              {isLoadingCategories && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" text="Загрузка категорий..." />
                </div>
              )}

              {categoriesError && (
                <ErrorMessage message="Ошибка загрузки категорий. Попробуйте обновить страницу." />
              )}

              {!isLoadingCategories &&
                !categoriesError &&
                categories.length === 0 && (
                  <div className="text-center py-8">
                    <i className="fas fa-list text-4xl text-base-content/30 mb-4"></i>
                    <p className="text-base-content/60">
                      {includeInactive
                        ? 'Категории не найдены'
                        : 'Пока нет добавленных категорий'}
                    </p>
                  </div>
                )}

              {!isLoadingCategories &&
                !categoriesError &&
                categories.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="table table-zebra">
                      <thead>
                        <tr>
                          <th>Название</th>
                          <th>Услуги</th>
                          <th>Статус</th>
                          <th>Порядок</th>
                          <th>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category: PriceCategoryWithItems) => (
                          <tr key={category.id}>
                            <td>
                              <Link
                                to={`/prices/categories/${category.id}/services`}
                                className="font-bold link link-hover text-primary"
                              >
                                {category.name}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/prices/categories/${category.id}/services`}
                                className="badge badge-info hover:badge-primary transition-colors"
                              >
                                {category.priceItems?.length || 0} услуг
                              </Link>
                            </td>
                            <td>
                              {category.isActive ? (
                                <span className="badge badge-success">
                                  Активна
                                </span>
                              ) : (
                                <span className="badge badge-error">
                                  Неактивна
                                </span>
                              )}
                            </td>
                            <td>{category.sortOrder}</td>
                            <td>
                              <div className="flex gap-2">
                                <Link
                                  to={`/prices/categories/${category.id}/services`}
                                  className="btn btn-info btn-sm"
                                  title="Просмотреть услуги"
                                >
                                  <i className="fas fa-list"></i>
                                </Link>
                                <Link
                                  to={`/prices/categories/${category.id}/edit`}
                                  className="btn btn-primary btn-sm"
                                  title="Редактировать"
                                >
                                  <i className="fas fa-edit"></i>
                                </Link>
                                <button
                                  onClick={() =>
                                    handleCategoryStatusChange(category)
                                  }
                                  className={`btn btn-sm ${
                                    category.isActive
                                      ? 'btn-warning'
                                      : 'btn-success'
                                  }`}
                                  title={
                                    category.isActive
                                      ? 'Деактивировать'
                                      : 'Активировать'
                                  }
                                >
                                  <i
                                    className={`fas ${category.isActive ? 'fa-pause' : 'fa-play'}`}
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <div className="card card-bordered bg-base-100 shadow-sm">
            <div className="card-body p-2">
              <h3 className="text-lg font-semibold text-base-content mb-4">
                Услуги
              </h3>

              {isLoadingItems && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" text="Загрузка услуг..." />
                </div>
              )}

              {itemsError && (
                <ErrorMessage message="Ошибка загрузки услуг. Попробуйте обновить страницу." />
              )}

              {!isLoadingItems && !itemsError && items.length === 0 && (
                <div className="text-center py-8">
                  <i className="fas fa-tags text-4xl text-base-content/30 mb-4"></i>
                  <p className="text-base-content/60">
                    {includeInactive
                      ? 'Услуги не найдены'
                      : 'Пока нет добавленных услуг'}
                  </p>
                </div>
              )}

              {!isLoadingItems && !itemsError && items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th>Название</th>
                        <th>Категория</th>
                        <th>Врач</th>
                        <th>Цена очно</th>
                        <th>Цена онлайн</th>
                        <th>Статус</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item: PriceItemWithRelations) => (
                        <tr key={item.id}>
                          <td>
                            <div className="font-bold">{item.name}</div>
                          </td>
                          <td>{item.category?.name || 'Не указана'}</td>
                          <td>
                            {item.doctor ? (
                              <span className="text-sm">
                                {item.doctor.fullName}
                              </span>
                            ) : (
                              <span className="text-base-content/40">
                                Не указан
                              </span>
                            )}
                          </td>
                          <td>{formatPrice(item.priceOffline)}</td>
                          <td>{formatPrice(item.priceOnline)}</td>
                          <td>
                            {item.isActive ? (
                              <span className="badge badge-success">
                                Активна
                              </span>
                            ) : (
                              <span className="badge badge-error">
                                Неактивна
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Link
                                to={`/prices/items/${item.id}/edit`}
                                className="btn btn-primary btn-sm"
                                title="Редактировать"
                              >
                                <i className="fas fa-edit"></i>
                              </Link>
                              <button
                                onClick={() => handleItemStatusChange(item)}
                                className={`btn btn-sm ${
                                  item.isActive ? 'btn-warning' : 'btn-success'
                                }`}
                                title={
                                  item.isActive
                                    ? 'Деактивировать'
                                    : 'Активировать'
                                }
                              >
                                <i
                                  className={`fas ${item.isActive ? 'fa-pause' : 'fa-play'}`}
                                ></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
